import { type FormEvent, Fragment, useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { ApiError, api } from "../../lib/api";
import type {
  PartnerAttributionRow,
  PartnerContractResponse,
  PartnerStatementResponse,
  PartnerStatusResponse,
} from "../../lib/api";
import { saveFile } from "../../lib/saveFile";
import { dollars } from "../Shopping/Shopping";
import styles from "./AccountSettings.module.css";

const errorText = (e: unknown, fallback: string): string =>
  e instanceof ApiError ? (e.body?.error ?? e.message) : fallback;

const dateLabel = (iso: string): string => new Date(iso).toLocaleDateString();

/** §1.1: the earning window is one clock from signature — an end date when windowed,
 * "lifetime" phrasing otherwise. */
const windowLabel = (partner: PartnerStatusResponse): string => {
  if (partner.duration_months === null) return "For the life of each subscription";
  if (partner.earning_ends_at) return `Until ${dateLabel(partner.earning_ends_at)}`;
  return `${partner.duration_months} months from signature`;
};

const downloadSignedPdf = async (onError: (message: string) => void): Promise<void> => {
  try {
    saveFile(await api.downloadPartnerContractPdf(), "shobdo-partner-agreement.pdf");
  } catch (e) {
    onError(errorText(e, "Couldn't download the signed agreement."));
  }
};

type PartnerTabProps = {
  partner: PartnerStatusResponse;
  onChanged: (partner: PartnerStatusResponse) => void;
};

/** The Account-page Partner tab (PRD PAYWALL §1.1): offered → the contract ceremony,
 * signed → agreement summary + signed-PDF copy + the monthly statement (§9 item 7),
 * ended → the ended state with the PDF still downloadable. */
export const PartnerTab = ({ partner, onChanged }: PartnerTabProps) => {
  const [justSigned, setJustSigned] = useState(false);

  if (partner.state === "offered") {
    return (
      <Ceremony
        onSigned={(updated) => {
          setJustSigned(true);
          onChanged(updated);
        }}
      />
    );
  }
  if (partner.state === "signed") {
    return <SignedPanel partner={partner} justSigned={justSigned} />;
  }
  return <EndedPanel partner={partner} />;
};

const Ceremony = ({ onSigned }: { onSigned: (partner: PartnerStatusResponse) => void }) => {
  const [contract, setContract] = useState<PartnerContractResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [typedName, setTypedName] = useState("");
  const [intent, setIntent] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const doc = await api.getPartnerContract();
        if (!cancelled) setContract(doc);
      } catch (e) {
        if (!cancelled) setLoadError(errorText(e, "Couldn't load the agreement."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSign = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSigning(true);
    setSignError(null);
    try {
      onSigned(await api.signPartnerContract({ typed_name: typedName.trim() }));
    } catch (e) {
      setSignError(errorText(e, "Couldn't sign the agreement."));
    } finally {
      setSigning(false);
    }
  };

  return (
    <section className={styles.panel}>
      <span className={styles.kicker}>Partnership agreement</span>
      <p className={styles.help}>
        Review &amp; sign your partnership agreement. Read the full document, then type your full
        legal name to sign it electronically.
      </p>
      {loadError && (
        <div role="alert" className={styles.error}>
          {loadError}
        </div>
      )}
      {contract && (
        <>
          <div className={styles.contractBox}>
            <pre className={styles.contractText}>{contract.contract_text}</pre>
          </div>
          <form className={styles.form} onSubmit={handleSign} noValidate>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="partner-typed-name">
                Type your full legal name
              </label>
              <Input
                id="partner-typed-name"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                disabled={signing}
                placeholder="Full legal name"
              />
            </div>
            <label className={styles.consentRow} htmlFor="partner-intent">
              <input
                id="partner-intent"
                type="checkbox"
                checked={intent}
                onChange={(e) => setIntent(e.target.checked)}
                disabled={signing}
              />
              <span className={styles.consentText}>
                I intend to sign this agreement electronically, and I agree to its terms.
              </span>
            </label>
            <div className={styles.actions}>
              <Button type="submit" disabled={signing || typedName.trim() === "" || !intent}>
                {signing ? "Signing…" : "Sign agreement"}
              </Button>
            </div>
            {signError && (
              <div role="alert" className={styles.error}>
                {signError}
              </div>
            )}
          </form>
        </>
      )}
    </section>
  );
};

const SignedPanel = ({
  partner,
  justSigned,
}: {
  partner: PartnerStatusResponse;
  justSigned: boolean;
}) => {
  const [rows, setRows] = useState<PartnerAttributionRow[] | null>(null);
  const [rowsError, setRowsError] = useState<string | null>(null);
  const [statement, setStatement] = useState<PartnerStatementResponse | null>(null);
  const [statementError, setStatementError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api.getPartnerAttributions();
        if (!cancelled) setRows(list.attributions);
      } catch (e) {
        if (!cancelled) setRowsError(errorText(e, "Couldn't load your stores."));
      }
      try {
        const monthly = await api.getPartnerStatement();
        if (!cancelled) setStatement(monthly);
      } catch (e) {
        if (!cancelled) setStatementError(errorText(e, "Couldn't load your statement."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <section className={styles.panel}>
        <span className={styles.kicker}>Partnership</span>
        {justSigned && (
          <p className={styles.ok}>
            {partner.contract_emailed_at
              ? "Agreement signed — a copy has been emailed to you."
              : "Agreement signed. Your signed copy is available below."}
          </p>
        )}
        <div className={styles.form}>
          <span className={styles.help}>
            Commission: {partner.rate_pct}% of collected subscription revenue.
          </span>
          <span className={styles.help}>Earning window: {windowLabel(partner)}.</span>
          {partner.signed_at && (
            <span className={styles.help}>Signed on {dateLabel(partner.signed_at)}.</span>
          )}
          <div className={styles.actions}>
            <Button
              type="button"
              variant="outline"
              onClick={() => void downloadSignedPdf(setDownloadError)}
            >
              Download signed agreement (PDF)
            </Button>
          </div>
          <span className={styles.help}>
            Payouts: earnings land in your Account Wallet — transfer to your bank from the Wallet
            tab.
          </span>
          {downloadError && (
            <div role="alert" className={styles.error}>
              {downloadError}
            </div>
          )}
        </div>
      </section>

      <section className={styles.panel}>
        <span className={styles.kicker}>Your stores</span>
        {rowsError && (
          <div role="alert" className={styles.error}>
            {rowsError}
          </div>
        )}
        {rows && rows.length === 0 && <p className={styles.help}>No stores attributed yet.</p>}
        {rows && rows.length > 0 && (
          <table className={styles.statTable}>
            <thead>
              <tr>
                <th>Store</th>
                <th>Attributed</th>
                <th>First paid month</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.store_name}>
                  <td>{row.store_name}</td>
                  <td>{dateLabel(row.attributed_at)}</td>
                  <td>{row.first_paid_month ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className={styles.panel}>
        <span className={styles.kicker}>Monthly statement</span>
        {statementError && (
          <div role="alert" className={styles.error}>
            {statementError}
          </div>
        )}
        {statement?.empty_note && <p className={styles.help}>{statement.empty_note}</p>}
        {statement && statement.months.length > 0 && (
          <>
            <table className={styles.statTable}>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Store</th>
                  <th>Collected</th>
                  <th>Your share</th>
                  <th>Clawbacks</th>
                  <th>Running share</th>
                </tr>
              </thead>
              <tbody>
                {statement.months.map((month) => (
                  <Fragment key={month.month}>
                    <tr>
                      <td>
                        <strong>{month.month}</strong>
                      </td>
                      <td>All stores</td>
                      <td>{dollars(month.collected_cents)}</td>
                      <td>{dollars(month.share_cents)}</td>
                      <td>
                        {month.clawback_cents > 0 ? `−${dollars(month.clawback_cents)}` : "—"}
                      </td>
                      <td>{dollars(month.running_share_cents)}</td>
                    </tr>
                    {month.stores.map((store) => (
                      <tr key={`${month.month}-${store.store_name}`}>
                        <td />
                        <td>{store.store_name}</td>
                        <td>{dollars(store.collected_cents)}</td>
                        <td />
                        <td />
                        <td />
                      </tr>
                    ))}
                  </Fragment>
                ))}
                <tr>
                  <td>
                    <strong>Totals</strong>
                  </td>
                  <td />
                  <td>{dollars(statement.total_collected_cents)}</td>
                  <td>{dollars(statement.total_share_cents)}</td>
                  <td>
                    {statement.total_clawback_cents > 0
                      ? `−${dollars(statement.total_clawback_cents)}`
                      : "—"}
                  </td>
                  <td>{dollars(statement.total_share_cents - statement.total_clawback_cents)}</td>
                </tr>
              </tbody>
            </table>
            <p className={styles.help}>
              Collected is what Shobdo actually collected from your stores' subscriptions; your
              share is {partner.rate_pct}% of collected.
            </p>
          </>
        )}
      </section>
    </>
  );
};

const EndedPanel = ({ partner }: { partner: PartnerStatusResponse }) => {
  const [downloadError, setDownloadError] = useState<string | null>(null);

  return (
    <section className={styles.panel}>
      <span className={styles.kicker}>Partnership</span>
      <p className={styles.help}>
        This partnership has ended
        {partner.ended_at ? ` (${dateLabel(partner.ended_at)})` : ""}. No further commission
        accrues.
      </p>
      {partner.pdf_available && (
        <div className={styles.actions}>
          <Button
            type="button"
            variant="outline"
            onClick={() => void downloadSignedPdf(setDownloadError)}
          >
            Download signed agreement (PDF)
          </Button>
        </div>
      )}
      {downloadError && (
        <div role="alert" className={styles.error}>
          {downloadError}
        </div>
      )}
    </section>
  );
};
